import {
  Dataset,
  catalogueStats,
  datasets,
  datasetsByCategory,
} from 'app/data/datasets';

/**
 * Places a dataset against the rest of the catalogue.
 *
 * Every claim here is derived from the registry at render time — nothing is
 * hardcoded, so "the deepest history in the catalogue" cannot go stale when a
 * dataset with a 1970 start is added. Where the underlying field is undefined
 * we say so rather than inferring a position.
 */

export interface ContextNote {
  /** Short label, e.g. "History". */
  label: string;
  /** The comparative sentence. */
  text: string;
  /** True when the dataset leads the catalogue on this dimension. */
  leading?: boolean;
  /** True when the claim rests on a field the registry has not published. */
  gap?: boolean;
}

const ordinal = (n: number): string => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

const yearOf = (iso: string): string => iso.slice(0, 4);

const CATEGORY_LABEL: Record<Dataset['category'], string> = {
  equity: 'equity',
  economic: 'economic',
  sectorial: 'sectorial',
};

export function buildCoverageContext(dataset: Dataset): ContextNote[] {
  const stats = catalogueStats();
  const notes: ContextNote[] = [];

  // ---- History depth --------------------------------------------------------
  const withHistory = datasets
    .filter(d => d.historyStart)
    .sort((a, b) =>
      (a.historyStart as string).localeCompare(b.historyStart as string),
    );

  if (dataset.historyStart) {
    const rank = withHistory.findIndex(d => d.slug === dataset.slug) + 1;
    const isDeepest = rank === 1;
    notes.push({
      label: 'History',
      leading: isDeepest,
      text: isDeepest
        ? `History begins ${yearOf(
            dataset.historyStart,
          )} — the deepest in the catalogue, across ${
            withHistory.length
          } datasets with a published start date.`
        : `History begins ${yearOf(dataset.historyStart)} — ${ordinal(
            rank,
          )} deepest of the ${
            withHistory.length
          } datasets with a published start date. The catalogue reaches back to ${
            stats.earliestHistory
              ? yearOf(stats.earliestHistory)
              : 'an earlier year'
          }.`,
    });
  } else {
    notes.push({
      label: 'History',
      gap: true,
      text: `No start date published for this dataset. ${
        withHistory.length
      } of ${
        stats.total
      } datasets in the catalogue have one; the deepest reaches ${
        stats.earliestHistory ? yearOf(stats.earliestHistory) : 'further back'
      }.`,
    });
  }

  // ---- Cross-section width --------------------------------------------------
  const withEntities = datasets
    .filter(d => typeof d.entities === 'number')
    .sort((a, b) => (b.entities as number) - (a.entities as number));

  if (typeof dataset.entities === 'number') {
    const rank = withEntities.findIndex(d => d.slug === dataset.slug) + 1;
    const isWidest = rank === 1;
    notes.push({
      label: 'Cross-section',
      leading: isWidest,
      text: isWidest
        ? `Covers ~${dataset.entities.toLocaleString(
            'en-US',
          )} entities — the widest coverage in the catalogue.`
        : `Covers ~${dataset.entities.toLocaleString(
            'en-US',
          )} entities — ${ordinal(rank)} widest of ${
            withEntities.length
          } datasets with a published entity count. The widest covers ~${stats.deepestEntities.toLocaleString(
            'en-US',
          )}.`,
    });
  } else {
    notes.push({
      label: 'Cross-section',
      gap: true,
      text: `No entity count published. ${withEntities.length} of ${
        stats.total
      } datasets have one, topping out at ~${stats.deepestEntities.toLocaleString(
        'en-US',
      )} entities.`,
    });
  }

  // ---- Category placement ---------------------------------------------------
  const siblings = datasetsByCategory(dataset.category);
  notes.push({
    label: 'Category',
    text: `One of ${siblings.length} ${
      CATEGORY_LABEL[dataset.category]
    } datasets, in a catalogue of ${stats.total}.`,
  });

  // ---- Maturity -------------------------------------------------------------
  notes.push({
    label: 'Status',
    gap: dataset.status === 'beta',
    text:
      dataset.status === 'beta'
        ? `In beta, alongside ${stats.beta - 1} other beta dataset${
            stats.beta - 1 === 1 ? '' : 's'
          }. Coverage figures are not yet fixed.`
        : `Generally available. ${stats.beta} of ${stats.total} datasets in the catalogue are still in beta.`,
  });

  return notes;
}

/** Related datasets: up to three others sharing this dataset's category. */
export function relatedDatasets(dataset: Dataset, limit = 3): Dataset[] {
  return datasetsByCategory(dataset.category)
    .filter(d => d.slug !== dataset.slug)
    .slice(0, limit);
}
